import { cloneElement, type ReactElement } from "react";

import { asCategoryType, categoryColor } from "@/lib/category-display";
import {
  CATEGORY_COLORS,
  CATEGORY_LABELS,
  type CategoryType,
  ENTITY_LABELS,
  type EntityType,
  type StatusIconName,
} from "@/lib/icons/categories";
import { cn } from "@/lib/utils";

/**
 * ============================================================================
 * THE SWAP POINT
 * ----------------------------------------------------------------------------
 * `EntityIcon` and `StatusIcon` are a stable seam: call sites only ever say
 * *what* (entity + category), never *how* (file path or colour).
 *
 * Today: artwork is optimised, per-combination vectors, so we resolve to a file
 * URL and render an <img>. `CATEGORY_COLORS` is wired through but the glyph
 * colour comes from the file (category colours are static — see README).
 *
 * If runtime tinting is ever needed, only the resolver below changes: swap the
 * <img> for an inlined per-entity glyph tinted via
 * `style={{ color: CATEGORY_COLORS[category] }}`. No call site changes.
 * See ../../lib/icons/README.md.
 * ============================================================================
 */

const ICON_BASE = "/icons";

/** Public URL of the pre-rendered entity×category artwork. */
function entityIconSrc(entity: EntityType, category: CategoryType): string {
  return `${ICON_BASE}/${entity}/${category}.svg`;
}

/** Public URL of a standalone status/tab icon. */
function statusIconSrc(name: StatusIconName): string {
  return `${ICON_BASE}/status/${name}.svg`;
}

interface EntityIconProps {
  entity: EntityType;
  /** Colour-only semantics: selects the tint, not the shape. */
  category: CategoryType;
  /** Rendered box size in px (square). Defaults to 40. */
  size?: number;
  className?: string;
  /** Overrides the default "<Entity> — <Category>" accessible label. */
  title?: string;
}

/**
 * Renders the icon for a compliance entity in its category colour.
 *
 * @example
 * <EntityIcon entity="audit" category="fire-safety" size={40} />
 */
export function EntityIcon({
  entity,
  category,
  size = 40,
  className,
  title,
}: EntityIconProps) {
  const label =
    title ?? `${ENTITY_LABELS[entity]} — ${CATEGORY_LABELS[category]}`;

  return (
    <img
      src={entityIconSrc(entity, category)}
      alt={label}
      title={label}
      width={size}
      height={size}
      loading="lazy"
      className={cn("inline-block object-contain", className)}
      // Exposed for Phase-2 recolouring / callers that tint surrounding chrome.
      data-category-color={CATEGORY_COLORS[category]}
    />
  );
}

interface TintedEntityIconProps {
  entity: EntityType;
  /** DB category slug; resolves to the fixed icon matrix when recognised. */
  slug?: string;
  /** Catalog colour for categories outside the fixed matrix. */
  color?: string;
  /** Rendered box size in px (square). Defaults to 32. */
  size?: number;
  /** Glyph shown in the neutral tinted square when the slug isn't in the matrix. */
  fallbackIcon: ReactElement<{ style?: React.CSSProperties }>;
  /** Accessible label, e.g. "Checklist — Fire Safety". */
  title?: string;
}

/**
 * Entity glyph tinted by category, with a graceful fallback for categories
 * outside the fixed icon matrix (e.g. admin-created ones): a rounded square
 * tinted with the catalog colour (or neutral grey) around `fallbackIcon`.
 * Same pattern as the checklist-manager and compliance-manager rows.
 */
export function TintedEntityIcon({
  entity,
  slug,
  color,
  size = 32,
  fallbackIcon,
  title,
}: TintedEntityIconProps) {
  const category = asCategoryType(slug);
  if (category) {
    return (
      <EntityIcon
        entity={entity}
        category={category}
        size={size}
        title={title}
      />
    );
  }
  const tint = categoryColor(slug, color);
  return (
    <span
      role="img"
      aria-label={title ?? ENTITY_LABELS[entity]}
      title={title ?? ENTITY_LABELS[entity]}
      className="inline-flex flex-none items-center justify-center rounded-md"
      style={{
        width: size,
        height: size,
        backgroundColor: tint ? `${tint}1A` : "#F1F1F1",
        color: tint ?? "#6B7280",
      }}
    >
      {cloneElement(fallbackIcon, {
        style: { width: size * 0.5, height: size * 0.5 },
      })}
    </span>
  );
}

interface StatusIconProps {
  name: StatusIconName;
  /** Rendered box size in px (square). Defaults to 24. */
  size?: number;
  className?: string;
  title?: string;
}

/**
 * Renders a standalone tab/status icon (Alerts, History, Failed checks, …).
 */
export function StatusIcon({
  name,
  size = 24,
  className,
  title,
}: StatusIconProps) {
  return (
    <img
      src={statusIconSrc(name)}
      alt={title ?? name}
      title={title ?? name}
      width={size}
      height={size}
      loading="lazy"
      className={cn("inline-block object-contain", className)}
    />
  );
}
